import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

type Feature = {
  label: string;
  status: "full" | "partial" | "hil";
  detail: string;
};

const FEATURES: Feature[] = [
  {
    label: "Full AAOS Build",
    status: "full",
    detail: "Complete Android Automotive OS",
  },
  { label: "Multi-Display", status: "full", detail: "Cluster + IVI" },
  {
    label: "Multi-User",
    status: "full",
    detail: "Driver, passenger, guest profiles",
  },
  {
    label: "UI/UX Flows",
    status: "full",
    detail: "Fully testable in emulator",
  },
  {
    label: "VHAL Properties",
    status: "partial",
    detail: "Most properties, some need stubs",
  },
  {
    label: "Sensor Simulation",
    status: "partial",
    detail: "GPS, accel, no real CAN bus",
  },
  { label: "Bluetooth", status: "partial", detail: "Testing in progress" },
  { label: "NFC", status: "hil", detail: "Requires hardware radio" },
  {
    label: "Camera Pipelines",
    status: "hil",
    detail: "Requires physical camera input",
  },
];

const STATUS_CONFIG = {
  full: {
    color: "#4ade80",
    bg: "rgba(74,222,128,0.12)",
    label: "Full Parity",
    pct: 100,
  },
  partial: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.12)",
    label: "Partial",
    pct: 65,
  },
  hil: {
    color: "#818cf8",
    bg: "rgba(129,140,248,0.12)",
    label: "HIL Only",
    pct: 0,
  },
};

export function BehavioralParitySlide() {
  const svgW = 900;
  const svgH = 480;
  const rowH = 42;
  const startY = 50;
  const labelX = 20;
  const barX = 240;
  const barW = 380;
  const statusX = 650;

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        <SlideTitle
          title="AAOS Behavioral Parity"
          subtitle="What works in cloud emulators vs. what requires hardware-in-the-loop"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Behavioral parity matrix showing emulator vs hardware capabilities"
          >
            {/* Header row */}
            <text
              x={barX}
              y={35}
              fontSize={10}
              fill="rgba(255,255,255,0.3)"
              fontFamily="inherit"
              fontWeight={600}
              letterSpacing="0.06em"
            >
              EMULATOR COVERAGE
            </text>
            <text
              x={statusX}
              y={35}
              fontSize={10}
              fill="rgba(255,255,255,0.3)"
              fontFamily="inherit"
              fontWeight={600}
              letterSpacing="0.06em"
            >
              STATUS
            </text>

            {FEATURES.map((feat, i) => {
              const y = startY + i * rowH;
              const cfg = STATUS_CONFIG[feat.status];
              const barFill = cfg.pct;

              return (
                <motion.g
                  key={feat.label}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.08, duration: 0.35 }}
                >
                  {/* Row background on hover-like alternation */}
                  {i % 2 === 0 && (
                    <rect
                      x={0}
                      y={y - 2}
                      width={svgW}
                      height={rowH}
                      rx={4}
                      fill="rgba(255,255,255,0.015)"
                    />
                  )}

                  {/* Feature label */}
                  <text
                    x={labelX}
                    y={y + 16}
                    fontSize={13}
                    fill="white"
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    {feat.label}
                  </text>
                  <text
                    x={labelX}
                    y={y + 30}
                    fontSize={10}
                    fill="rgba(255,255,255,0.4)"
                    fontFamily="inherit"
                  >
                    {feat.detail}
                  </text>

                  {/* Bar track */}
                  <rect
                    x={barX}
                    y={y + 8}
                    width={barW}
                    height={18}
                    rx={9}
                    fill="rgba(255,255,255,0.04)"
                  />

                  {/* Bar fill */}
                  {barFill > 0 && (
                    <motion.rect
                      x={barX}
                      y={y + 8}
                      height={18}
                      rx={9}
                      fill={cfg.color}
                      opacity={0.25}
                      initial={{ width: 0 }}
                      animate={{ width: (barW * barFill) / 100 }}
                      transition={{
                        delay: 0.6 + i * 0.08,
                        duration: 0.5,
                        ease: "easeOut",
                      }}
                    />
                  )}

                  {/* Bar fill inner glow */}
                  {barFill > 0 && (
                    <motion.rect
                      x={barX + 2}
                      y={y + 10}
                      height={14}
                      rx={7}
                      fill={cfg.color}
                      opacity={0.15}
                      initial={{ width: 0 }}
                      animate={{
                        width: Math.max(0, (barW * barFill) / 100 - 4),
                      }}
                      transition={{
                        delay: 0.65 + i * 0.08,
                        duration: 0.5,
                        ease: "easeOut",
                      }}
                    />
                  )}

                  {/* Status badge */}
                  <rect
                    x={statusX}
                    y={y + 8}
                    width={88}
                    height={22}
                    rx={11}
                    fill={cfg.bg}
                  />
                  <text
                    x={statusX + 44}
                    y={y + 23}
                    textAnchor="middle"
                    fontSize={10}
                    fill={cfg.color}
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    {cfg.label}
                  </text>
                </motion.g>
              );
            })}

            {/* Legend at bottom */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              {Object.entries(STATUS_CONFIG).map(([, cfg], i) => {
                const lx = 200 + i * 200;
                const ly = svgH - 30;
                return (
                  <g key={cfg.label}>
                    <circle
                      cx={lx}
                      cy={ly}
                      r={5}
                      fill={cfg.color}
                      opacity={0.6}
                    />
                    <text
                      x={lx + 12}
                      y={ly + 4}
                      fontSize={10}
                      fill="rgba(255,255,255,0.5)"
                      fontFamily="inherit"
                    >
                      {cfg.label}
                    </text>
                  </g>
                );
              })}
            </motion.g>
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
